import * as React from "react";
import { IconChevronDown } from "@tabler/icons-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

/**
 * The pieces every settings tab is built from: a form column of stacked
 * cards, an aside that shows the result of what is being edited, and one
 * save band that appears only once something changed.
 */

/** Form on the left, a sticky aside on the right; stacked on small screens. */
export function SettingsGrid({
  form,
  aside,
  fillAside = false,
}: {
  form: React.ReactNode;
  aside: React.ReactNode;
  /** The aside stretches to the form column's height instead of sticking;
   *  use it when the aside is one card whose foot should line up. */
  fillAside?: boolean;
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-12">
      <div className="min-w-0 space-y-4 lg:col-span-7">{form}</div>
      <div className="min-w-0 lg:col-span-5">
        <div className={cn("space-y-4", fillAside ? "lg:flex lg:h-full lg:flex-col" : "lg:sticky lg:top-4")}>
          {aside}
        </div>
      </div>
    </div>
  );
}

/** One labelled control: label above, control, one optional line of help. */
export function Field({
  id,
  htmlFor,
  label,
  help,
  trailing,
  children,
  className,
}: {
  id?: string;
  htmlFor?: string;
  label: string;
  help?: React.ReactNode;
  /** Something small at the label's right edge, such as a character count. */
  trailing?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div id={id} className={cn("space-y-1.5", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <Label htmlFor={htmlFor} className="text-foreground">
          {label}
        </Label>
        {trailing && <span className="text-xs tabular-nums text-muted-foreground">{trailing}</span>}
      </div>
      {children}
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
    </div>
  );
}

/** Two fields side by side from the sm breakpoint. */
export function FieldRow({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}

/**
 * The save band. It exists only while there are changes and sits under the
 * aside's preview card, so the result of the edit and the button that
 * keeps it are in one place, in view while the form scrolls.
 */
export function UnsavedBar({
  visible,
  saving,
  onCancel,
  saveId,
  saveLabel = "Save changes",
}: {
  visible: boolean;
  saving: boolean;
  onCancel: () => void;
  saveId?: string;
  saveLabel?: string;
}) {
  if (!visible) return null;
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3">
      <span className="text-sm text-muted-foreground">Unsaved changes</span>
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button id={saveId} type="submit" disabled={saving}>
          {saving && <Spinner size="sm" className="border-primary-foreground border-t-transparent" />}
          {saveLabel}
        </Button>
      </div>
    </div>
  );
}

/**
 * One section open at a time: the first starts open, opening another
 * closes it, and opening the open one closes it. Everything opens while a
 * product tour runs so its anchors are on screen.
 */
export function useSections(initiallyOpen: string, forceOpen = false) {
  const [open, setOpen] = React.useState<string | null>(initiallyOpen);
  return {
    isOpen: (id: string) => forceOpen || open === id,
    toggle: (id: string) => setOpen((prev) => (prev === id ? null : id)),
  };
}

/**
 * A collapsible settings card. The whole header is the control: the title
 * on the left and a chevron on the right that turns when the section opens.
 */
export function SettingsSection({
  id,
  title,
  open,
  onToggle,
  bodyClassName,
  children,
}: {
  id?: string;
  title: string;
  open: boolean;
  onToggle: () => void;
  bodyClassName?: string;
  children: React.ReactNode;
}) {
  const bodyId = React.useId();
  return (
    <section id={id} className="overflow-hidden rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <span className="text-[15px] font-semibold text-foreground">{title}</span>
        <IconChevronDown
          size={16}
          stroke={1.75}
          aria-hidden
          className={cn(
            "shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none",
            open && "rotate-180"
          )}
        />
      </button>
      {open && (
        <div id={bodyId} className={cn("border-t border-border px-5 py-5", bodyClassName)}>
          {children}
        </div>
      )}
    </section>
  );
}

/** A quiet key and value row for the aside cards. */
export function AsideRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-t border-border py-2.5 text-sm first:border-t-0">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right text-foreground">{children}</span>
    </div>
  );
}
